import { useEffect, useState, useContext, useRef } from "react";
import toast from "react-hot-toast";
import PaymentTable from "./components/PaymentsList";
import { getAllClients } from "api/clients";
import { ClientContext } from "ClientProvider";
import { getSingleRole } from "api/graphQL_role";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { getCardType } from "api/graphQL_payments";
import { getId } from "utils/auth";
import { getPayoutsFilter } from "api/payouts";
import BatchPayout from "views/admin/batch/payout";

export type PaymentRowObj = {
  id: string;
  payment_id: string;
  et_id: string;
  amount: string;
  currency: string;
  gateway_id: string;
  gateway_name: string;
  status: string;
  meta_info: any;
  gateway_response: any;
  payment_type: string;
  client: string;
  refund_info: string;
  created_at: string;
  image: string;
  all_data: any[];
  expanded: boolean;
  payment_history: any;
};

const Payouts = () => {
  const [payments, setPayments] = useState<PaymentRowObj[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>();
  const [page, setPage] = useState<number>(0);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [clients, setClients] = useState();
  const [value, setValue] = useState(false);
  const [curretClient, setCurrentClient] = useState<string>("all");
  const [allGateways, setAllGateways] = useState();
  const [allCardType, setCardType] = useState();
  const [searchTexts, setSearchTexts] = useState<string>("");
  const [localFilters, setLocalFilters] = useState<any>({});
  const [clearClicked, setClearClick] = useState(0);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>();
  const { activeTab, setActiveTab } = useContext(ClientContext);
  const {
    paymentType, singleRoleData, setSingleRoleData,
    filter, setFilter, setLoadingTransaction, loadingTransaction,
  } = useContext(ClientContext);

  useEffect(() => { setFilter({}); }, []);

  const [loadingState, setLoadingState] = useState({
    clients: true, gateways: true, cardTypes: true,
  });

  const hasSinglePayoutPermission = singleRoleData?.[0]?.payout?.value?.view_payout_list;
  const hasBatchPayoutPermission  = singleRoleData?.[0]?.payout?.value?.view_batch_payout;
  const apiCalledRef = useRef(false);

  const fetchClients = () => {
    getAllClients()
      .then((data) => setClients(data))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong while fetching clients"))
      .finally(() => setLoadingState(prev => ({ ...prev, clients: false })));
  };

  const fetchAllGateways = () => {
    getAllGatewaysSecond()
      .then((data) => setAllGateways(data))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong while fetching gateways"))
      .finally(() => setLoadingState(prev => ({ ...prev, gateways: false })));
  };

  const fetchCardType = () => {
    getCardType()
      .then((data) => setCardType(data))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong while fetching card types"))
      .finally(() => setLoadingState(prev => ({ ...prev, cardTypes: false })));
  };

  useEffect(() => {
    fetchClients();
    fetchAllGateways();
    fetchCardType();
  }, []);

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      await setSearchTexts(search_txt);
      setPage(1);
    } else {
      await setSearchTexts(search_txt);
    }
  };

  const filterData = async () => {
    const paymentType   = filter?.status;
    const client        = filter?.client;
    const gateway       = filter?.gateway;
    const payment_status = filter?.payment_status;
    const card_type     = filter?.card_type;
    const start_date    = filter?.dates?.[0];
    const end_date      = filter?.dates?.[1];
    const timezone      = filter?.timezone;

    setIsPaymentLoading(true);
    setPayments([]);
    setLoadingTransaction(true);
    try {
      const data = await getPayoutsFilter(
        page, pageSize, paymentType, client, gateway,
        card_type, payment_status, timezone, start_date, searchTexts, end_date
      );
      setPayments(
        Object.entries(data.payments ?? {}).map(([key, value]: any) => ({
          ...value[0], payment_id: key, expanded: false, all_data: value,
        }))
      );
      setTotalPage(data.total_pages);
      setTotalItems(data.total_items);
      setCurrentPage(data.current_page);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Something went wrong while fetching payouts");
    } finally {
      setIsPaymentLoading(false);
      setLoadingTransaction(false);
    }
  };

  useEffect(() => {
    if (page === 0) return;
    filterData();
  }, [page, curretClient, pageSize, clearClicked, activeTab, searchTexts, filter?.status]);

  useEffect(() => {
    apiCalledRef.current = false;
    setPage(1);
    setValue(!value);
  }, [paymentType, filter?.status, searchTexts, activeTab]);

  const onClearClick = () => {
    setPage(1);
    setPageSize(50);
    setClearClick((prev) => prev + 1);
  };

  const isAllDataReady = Object.values(loadingState).every(v => v === false);

  const tableProps = {
    pageSize, setPageSize, tableData: payments, fetchPayments: filterData,
    isLoading: isPaymentLoading, page, setPage, totalpage, totalItems,
    currentPage, clients, allGateways, allCardType, curretClient,
    setCurrentClient, roleData: singleRoleData, filterData,
    setClearClick: onClearClick, filter, setFilter,
    onValueChange: serchboxValueChange, setLocalFilters, isAllDataReady,searchTexts
  };

  return (
    <div className="po-root">

      {/* ── Page header ── */}
      <div className="po-page-header">
        <div className="po-title-block">
          <div className="po-page-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 12V22H4V12"/>
              <path d="M22 7H2v5h20V7z"/>
              <path d="M12 22V7"/>
              <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
              <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
            </svg>
          </div>
          <div>
            <h1 className="po-heading">Payouts</h1>
            <p className="po-sub">Manage single and batch payout transactions</p>
          </div>
        </div>
      </div>

      {/* ── Both tabs ── */}
      {hasSinglePayoutPermission && hasBatchPayoutPermission ? (
        <>
          {/* Tab toggle */}
          <div className="po-tab-wrap">
            <div className="po-tab-track">
              <div
                className="po-tab-slider"
                style={{
                  transform: activeTab === "single" ? "translateX(0%)" : "translateX(100%)",
                }}
              />
              <button
                onClick={() => { setActiveTab("single"); setFilter({}); setPageSize(50); }}
                className={`po-tab-btn${activeTab === "single" ? " po-tab-btn--active" : ""}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V22H4V12"/>
                  <path d="M22 7H2v5h20V7z"/>
                  <path d="M12 22V7"/>
                </svg>
                Single Payout
              </button>
              <button
                onClick={() => setActiveTab("batch")}
                className={`po-tab-btn${activeTab === "batch" ? " po-tab-btn--active" : ""}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2"/>
                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                  <line x1="12" y1="12" x2="12" y2="16"/>
                  <line x1="10" y1="14" x2="14" y2="14"/>
                </svg>
                Batch Payout
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="po-content-card">
            {activeTab === "single" && <PaymentTable {...tableProps} />}
            {activeTab === "batch"  && <BatchPayout />}
          </div>
        </>

      ) : hasSinglePayoutPermission ? (
        <div className="po-content-card">
          <PaymentTable {...tableProps} />
        </div>

      ) : hasBatchPayoutPermission ? (
        <div className="po-content-card">
          <BatchPayout />
        </div>

      ) : null}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* ── Root ── */
        .po-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          display: flex; flex-direction: column; gap: 20px;
        }

        /* ── Page header ── */
        .po-page-header {
          display: flex; align-items: center;
          justify-content: space-between; flex-wrap: wrap; gap: 16px;
        }
        .po-title-block { display: flex; align-items: center; gap: 14px; }
        .po-page-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .po-heading {
          font-size: 20px; font-weight: 800; color: #0f172a;
          letter-spacing: -0.4px; margin-bottom: 2px; line-height: 1.2;
        }
        .po-sub { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* ── Tab toggle ── */
        .po-tab-wrap { display: flex; }
        .po-tab-track {
          position: relative; display: flex;
          background: #f1f5f9; border: 1.5px solid #e2e8f0;
          border-radius: 12px; padding: 4px; gap: 0;
        }
        .po-tab-slider {
          position: absolute; top: 4px; bottom: 4px;
          width: calc(50% - 4px);
          background: #fff; border-radius: 9px;
          box-shadow: 0 2px 8px rgba(15,23,42,.1);
          transition: transform .25s cubic-bezier(0.16,1,0.3,1);
          pointer-events: none;
        }
        .po-tab-btn {
          position: relative;
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748b;
          padding: 10px 28px; border: none; background: none;
          border-radius: 9px; cursor: pointer; transition: color .2s;
          white-space: nowrap;
        }
        .po-tab-btn--active { color: #2563eb; font-weight: 700; }
        .po-tab-btn:hover:not(.po-tab-btn--active) { color: #334155; }

        /* ── Content card ── */
        .po-content-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
          overflow: hidden;
        }

        @media (max-width: 600px) {
          .po-tab-btn { padding: 10px 16px; font-size: 12.5px; }
          .po-root { padding: 16px 0; }
        }
      `}</style>
    </div>
  );
};

export default Payouts;